import { useEffect, useState } from "react";

/** @see https://tailwindcss.com/docs/responsive-design#overview */
export const standardMediaQueries = {
  sm: "(min-width: 40rem)",
  md: "(min-width: 48rem)",
  lg: "(min-width: 64rem)",
  xl: "(min-width: 80rem)",
  "2xl": "(min-width: 96rem)",
} as const;

/**
 * Checks if the current window matches a given standard media query.
 *
 * This is preferred, but if you need to check a custom media query string, use {@link useCustomMediaQuery}.
 *
 * @see {@link standardMediaQueries}
 */
export const useMediaQuery = (query: keyof typeof standardMediaQueries): boolean => {
  return useCustomMediaQuery(standardMediaQueries[query]);
};

/**
 * Checks if the current window matches a given media query string. Takes any media query string.
 *
 * Generally, you should use.
 * @see {@link standardMediaQueries}
 */
export const useCustomMediaQuery = (
  /** The media query string (e.g., '(min-width: 768px)') */
  query: string,
): boolean => {
  // Initialize state based on current window status, default to false if no window
  const [matches, setMatches] = useState<boolean>(() => {
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);

    // Listener to update state on change
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add listener
    mediaQueryList.addEventListener("change", handleChange);

    // Cleanup: remove listener on unmount
    return () => {
      mediaQueryList.removeEventListener("change", handleChange);
    };
  }, [query]); // Re-run effect if the query string changes

  return matches;
};
